import React, { useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import DmConversationList from "./DmConversationList";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "dm-sections-collapsed";

const DEFAULT_COLLAPSED = {
  direct: false,
  groups: false,
  channels: false,
  partners: false,
};

function loadCollapsed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLLAPSED };
    const parsed = JSON.parse(raw);
    return {
      direct: !!parsed?.direct,
      groups: !!parsed?.groups,
      channels: !!parsed?.channels,
      partners: !!parsed?.partners,
    };
  } catch {
    return { ...DEFAULT_COLLAPSED };
  }
}

function unreadTotal(list) {
  return (list || []).reduce((sum, c) => {
    if (c.muted) return sum;
    return sum + (Number(c.unread_count) || 0);
  }, 0);
}

function Section({
  id,
  title,
  conversations,
  collapsed,
  onToggle,
  selectedId,
  onSelect,
  onToggleMute,
  emptyMessage,
  listClassName,
  borderTop,
}) {
  const unread = unreadTotal(conversations);
  return (
    <div
      className={cn(
        "flex flex-col min-h-0 overflow-hidden",
        borderTop && "border-t border-border"
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex-1 truncate">
          {title}
        </span>
        {unread > 0 && (
          <Badge
            variant="destructive"
            className="h-4 min-w-4 px-1 text-[10px] shrink-0"
          >
            {unread > 99 ? "99+" : unread}
          </Badge>
        )}
      </button>
      {!collapsed && (
        <div className="min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
          <DmConversationList
            conversations={conversations}
            loading={false}
            selectedId={selectedId}
            onSelect={onSelect}
            onToggleMute={onToggleMute}
            emptyMessage={emptyMessage}
            className={listClassName}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Three independently scrollable, collapsible conversation sections.
 * Expanded sections share leftover height equally (minmax 0 1fr).
 */
export default function DmConversationSections({
  conversations,
  loading,
  selectedId,
  onSelect,
  onToggleMute,
  listClassName,
  className,
  partnerEmptyMessage,
}) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  const toggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const { direct, groups, channels, partners } = useMemo(() => {
    const list = conversations || [];
    return {
      direct: list.filter(
        (c) =>
          c.type !== "group" &&
          c.type !== "channel" &&
          c.type !== "partner"
      ),
      groups: list.filter((c) => c.type === "group"),
      channels: list.filter((c) => c.type === "channel"),
      partners: list.filter((c) => c.type === "partner"),
    };
  }, [conversations]);

  // Collapsed = header only. Expanded = share remaining height and scroll inside.
  const gridTemplateRows = useMemo(() => {
    const keys = ["direct", "groups", "channels", "partners"];
    return keys
      .map((k) => (collapsed[k] ? "auto" : "minmax(0, 1fr)"))
      .join(" ");
  }, [collapsed]);

  if (loading) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground", className)}>
        Loading…
      </div>
    );
  }

  const sectionProps = {
    onToggle: toggle,
    selectedId,
    onSelect,
    onToggleMute,
    listClassName,
  };

  return (
    <div
      className={cn("grid min-h-0 h-full", className)}
      style={{ gridTemplateRows }}
    >
      <Section
        id="direct"
        title="Direct"
        conversations={direct}
        collapsed={collapsed.direct}
        emptyMessage="No direct messages yet."
        {...sectionProps}
      />
      <Section
        id="groups"
        title="Groups"
        conversations={groups}
        collapsed={collapsed.groups}
        emptyMessage="No groups yet."
        borderTop
        {...sectionProps}
      />
      <Section
        id="channels"
        title="Channels"
        conversations={channels}
        collapsed={collapsed.channels}
        emptyMessage="No channels yet. Open Channel Browser in the sidebar to browse."
        borderTop
        {...sectionProps}
      />
      <Section
        id="partners"
        title="Partner"
        conversations={partners}
        collapsed={collapsed.partners}
        emptyMessage={
          partnerEmptyMessage ??
          "No partner channels yet. Ask a workspace admin to set up Partner spaces."
        }
        borderTop
        {...sectionProps}
      />
    </div>
  );
}
